import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  AddDismantlingWorkEntryInput,
  CancelledDismantlingSession,
  CreateDismantlingSessionInput,
  DismantlingCancelPreview,
  DismantlingCancelResult,
  DismantlingIntermediateDto,
  DismantlingPackageDto,
  DismantlingSessionDetailDto,
  UpdateDismantlingSessionInput,
} from './types';

export interface DismantlingListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface CancelledDismantlingSessionListResult {
  data: CancelledDismantlingSession[];
  meta: DismantlingListMeta;
}

export interface DismantlingPackageFilters {
  processed?: 'true' | 'false' | 'all';
  route_type?: string;
  supplier?: string;
  page?: number;
  per_page?: number;
  /**
   * Comma-separated inbound_package_ids (max 200). When supplied the backend
   * returns exactly those packages that are dismantling-eligible in the batch,
   * ignoring the processed / route_type / supplier filters and pagination —
   * this is how a deep link resolves pallets that are not in the list window.
   * Ineligible or unknown ids are simply absent from the response.
   */
  inbound_package_ids?: string;
}

export const fetchDismantlingPackages = async (
  batchId: string,
  params: DismantlingPackageFilters = {},
): Promise<{ data: DismantlingPackageDto[]; meta: DismantlingListMeta }> => {
  const res = await apiClient.get<ApiSuccess<DismantlingPackageDto[], DismantlingListMeta>>(
    `/monthly-batches/${batchId}/dismantling-packages`,
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

export const fetchDismantlingSession = async (id: string): Promise<DismantlingSessionDetailDto> => {
  const res = await apiClient.get<ApiSuccess<DismantlingSessionDetailDto>>(
    `/dismantling-sessions/${id}`,
  );
  return res.data.data;
};

export const createDismantlingSession = async (
  body: CreateDismantlingSessionInput,
): Promise<DismantlingSessionDetailDto> => {
  const res = await apiClient.post<ApiSuccess<DismantlingSessionDetailDto>>(
    `/dismantling-sessions`,
    body,
  );
  return res.data.data;
};

export const updateDismantlingSession = async (
  id: string,
  body: UpdateDismantlingSessionInput,
): Promise<DismantlingSessionDetailDto> => {
  const res = await apiClient.put<ApiSuccess<DismantlingSessionDetailDto>>(
    `/dismantling-sessions/${id}`,
    body,
  );
  return res.data.data;
};

export const addDismantlingWorkEntry = async (
  id: string,
  body: AddDismantlingWorkEntryInput,
): Promise<DismantlingSessionDetailDto> => {
  const res = await apiClient.post<ApiSuccess<DismantlingSessionDetailDto>>(
    `/dismantling-sessions/${id}/work-entries`,
    body,
  );
  return res.data.data;
};

export const voidDismantlingWorkEntry = async (
  sessionId: string,
  workEntryId: string,
  reason: string,
): Promise<DismantlingSessionDetailDto> => {
  const res = await apiClient.post<ApiSuccess<DismantlingSessionDetailDto>>(
    `/dismantling-sessions/${sessionId}/work-entries/${workEntryId}/void`,
    { reason },
  );
  return res.data.data;
};

export const confirmDismantlingSession = async (
  id: string,
  change_reason?: string,
): Promise<DismantlingSessionDetailDto> => {
  const res = await apiClient.post<ApiSuccess<DismantlingSessionDetailDto>>(
    `/dismantling-sessions/${id}/confirm`,
    { change_reason },
  );
  return res.data.data;
};

export const previewDismantlingSessionCancel = async (
  id: string,
): Promise<DismantlingCancelPreview> => {
  const res = await apiClient.get<ApiSuccess<DismantlingCancelPreview>>(
    `/dismantling-sessions/${id}/cancel-preview`,
  );
  return res.data.data;
};

export const cancelDismantlingSession = async (
  id: string,
  reason: string,
): Promise<DismantlingCancelResult> => {
  const res = await apiClient.post<ApiSuccess<DismantlingCancelResult>>(
    `/dismantling-sessions/${id}/cancel`,
    { reason },
  );
  return res.data.data;
};

export const fetchCancelledDismantlingSessions = async (
  params: { monthly_batch_id?: string; page?: number; per_page?: number } = {},
): Promise<CancelledDismantlingSessionListResult> => {
  const res = await apiClient.get<ApiSuccess<CancelledDismantlingSession[], DismantlingListMeta>>(
    '/dismantling-sessions/cancelled',
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

export const restoreCancelledDismantlingSession = async (
  archiveId: string,
): Promise<DismantlingCancelResult> => {
  const res = await apiClient.post<ApiSuccess<DismantlingCancelResult>>(
    `/dismantling-sessions/cancelled/${archiveId}/restore`,
  );
  return res.data.data;
};

export const confirmDismantlingIntermediate = async (
  id: string,
): Promise<DismantlingIntermediateDto> => {
  const res = await apiClient.post<ApiSuccess<DismantlingIntermediateDto>>(
    `/dismantling-intermediates/${id}/confirm`,
    {},
  );
  return res.data.data;
};

export const voidDismantlingIntermediate = async (
  id: string,
  reason: string,
): Promise<DismantlingIntermediateDto> => {
  const res = await apiClient.post<ApiSuccess<DismantlingIntermediateDto>>(
    `/dismantling-intermediates/${id}/void`,
    { reason },
  );
  return res.data.data;
};
